import { ShieldCheck, RefreshCw } from "lucide-react";
import PhoneMockup from "@/components/PhoneMockup";
import GooglePlayBadge from "@/components/GooglePlayBadge";
import { trackEvent } from "@/lib/analytics";
import { useT } from "@/i18n/LanguageContext";

/**
 * VirtueGuideSection — dedicated premium launch section for the Virtue Guide
 * mobile companion app. Sits directly underneath the "Create your free
 * My StoicOs" block on the homepage. Deliberately separate from that card:
 *
 *   • My StoicOs   = your personal account and learning environment.
 *   • Virtue Guide = your mobile companion app.
 *
 * Official Apple App Store + Google Play badges with equal prominence,
 * a sync statement and a trust line, in all five site languages.
 */

const APPSTORE_URL = process.env.REACT_APP_APPSTORE_URL || "";
const serif = { fontFamily: "'Cormorant Garamond','EB Garamond',Georgia,serif" };
const BADGE_H = 54;

export default function VirtueGuideSection() {
  const { t, lang } = useT();
  const appleBadgeSrc = lang === "nl" ? "/badges/app-store-nl.svg" : "/badges/app-store-en.svg";

  return (
    <section
      id="virtue-guide-app"
      data-testid="home-virtueguide-section"
      className="max-w-5xl mx-auto px-6 md:px-10 pt-16 md:pt-20"
    >
      <div className="relative border border-gold/25 bg-[#0b0a08] overflow-hidden">
        {/* soft gold glow */}
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-gold/[0.07] blur-3xl pointer-events-none" aria-hidden />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/50 to-transparent" aria-hidden />

        <div className="grid md:grid-cols-12 items-center">
          <div className="md:col-span-7 p-8 md:p-12 lg:p-14">
            <p className="text-[10px] tracking-[0.32em] uppercase text-gold" data-testid="vgs-eyebrow">
              {t("vgs.eyebrow")}
            </p>
            <h2 style={serif} className="mt-5 text-3xl md:text-4xl text-bone leading-[1.1]" data-testid="vgs-title">
              {t("vgs.title1")} <span className="italic text-gold">{t("vgs.title2")}</span>
            </h2>
            <p className="mt-6 text-bone/75 font-light leading-[1.85]" data-testid="vgs-body">
              {t("vgs.body")}
            </p>
            <p className="mt-5 flex items-start gap-3 text-bone/65 font-light leading-relaxed text-sm" data-testid="vgs-sync">
              <RefreshCw size={15} strokeWidth={1.5} className="text-gold mt-1 shrink-0" aria-hidden />
              <span>{t("vgs.sync")}</span>
            </p>

            {/* Official store badges — equal prominence */}
            <div id="download" className="mt-9 flex flex-col sm:flex-row items-center sm:items-start gap-4" data-testid="vgs-badges">
              <a
                href={APPSTORE_URL || "#"}
                target={APPSTORE_URL ? "_blank" : undefined}
                rel={APPSTORE_URL ? "noopener noreferrer" : undefined}
                data-testid="vgs-appstore-cta"
                onClick={() => trackEvent("app_store_click", { placement: "vg_section_appstore", has_store_url: !!APPSTORE_URL })}
                aria-label="Download Virtue Guide on the App Store"
                className="inline-block transition-transform duration-300 hover:scale-[1.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
                style={{ lineHeight: 0 }}
              >
                <img
                  src={appleBadgeSrc}
                  alt="Download on the App Store"
                  style={{ height: `${BADGE_H}px`, width: "auto", display: "block" }}
                  draggable={false}
                />
              </a>
              <GooglePlayBadge
                visibleHeight={BADGE_H}
                placement="vg_section_playstore"
                testid="vgs-playstore-cta"
              />
            </div>

            <p className="mt-7 flex items-center gap-2.5 text-[10px] tracking-[0.22em] uppercase text-bone/50" data-testid="vgs-trust">
              <ShieldCheck size={14} strokeWidth={1.5} className="text-gold shrink-0" aria-hidden />
              {t("vgs.trust")}
            </p>
          </div>

          {/* Phone mock-up — cropped launch presentation (desktop only) */}
          <div className="md:col-span-5 hidden md:block relative h-[460px] overflow-hidden self-stretch">
            <div className="absolute top-10 left-1/2 -translate-x-1/2">
              <PhoneMockup />
            </div>
            <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-[#0b0a08] to-transparent pointer-events-none" aria-hidden />
          </div>
        </div>
      </div>
    </section>
  );
}
